import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ExportFormatCode, type ClientFilter } from '@/core/models/practice.models';
import { Button } from '@/kit/Button';
import { FormField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { Select, SelectOption } from '@/kit/Select';
import { useDebouncedSearch } from '../useDebouncedSearch';

interface ClientHeaderProps {
  /** Angular `filterChanged`: only the changed field is emitted; the list merges it into its query. */
  onFilterChanged: (filter: ClientFilter) => void;
  /** Angular `exportRequested`. */
  onExportRequested: (format: ExportFormatCode) => void;
}

/** Angular `ClientHeaderComponent`: the Clients toolbar (Add button, search, three filters, export menu). */
export function ClientHeader({ onFilterChanged, onExportRequested }: ClientHeaderProps) {
  const navigate = useNavigate();
  // -1 is the "all" option on each of these, so every filter opens on a selected value rather than blank.
  const [clientTypeFilter, setClientTypeFilter] = useState(-1);
  /** -1 all, 1 active only, 0 inactive only; the list opens on the Active clients. */
  const [clientStatusFilter, setClientStatusFilter] = useState(1);
  /** ap5-a3: 0 shows the live clients, 1 shows the deleted ones. */
  const [recordStatusFilter, setRecordStatusFilter] = useState(0);

  const onSearch = useDebouncedSearch((searchText) => onFilterChanged({ searchText }));

  // CommonService.onAddRouting(Modules.Clients)
  const onAddClick = () => void navigate('/home/manage/addClient');

  const onClientTypeChange = (value: number | null) => {
    const next = value ?? -1;
    setClientTypeFilter(next);
    onFilterChanged({ clientType: next < 0 ? null : next });
  };

  /** Active/Inactive is the client's own status, separate from the deleted-records filter. */
  const onClientStatusChange = (value: number | null) => {
    const next = value ?? -1;
    setClientStatusFilter(next);
    onFilterChanged({ isActive: next < 0 ? null : next === 1 });
  };

  /**
   * ap5-a3: deleted clients are kept, so the grid can list them for review or restore.
   * APT240: switching between the live and the deleted list puts Active/Inactive back on Active (live list) or drops it
   * from the query (deleted list), so a status picked for the live list no longer narrows the deleted one (3.0 had
   * Deleted as its own status value, never combined with Active/Inactive) and coming back starts unfiltered too.
   */
  const onRecordStatusChange = (value: number | null) => {
    const next = value ?? 0;
    setRecordStatusFilter(next);
    // the live list comes back on Active; the deleted list has no Active/Inactive of its own
    setClientStatusFilter(next === 0 ? 1 : -1);
    onFilterChanged({ recordStatus: next, isActive: next === 0 ? true : null });
  };

  /** APT240: a deleted client is just deleted (as in 3.0), so Active/Inactive does not apply to that list. */
  const showingDeleted = recordStatusFilter === 1;

  return (
    <div>
      <div className="content-header mb-0">
        <div className="content-header-left mb-1">
          <Button className="action-button primary-button" onClick={onAddClick}>
            <Icon name="add" />
            <span>Add Client</span>
          </Button>
          <FormField className="search-field">
            <Icon name="search" />
            <input
              className="mat-mdc-input-element mdc-text-field__input mat-mdc-form-field-input-control"
              placeholder="Search"
              autoComplete="off"
              maxLength={100}
              onInput={(event) => onSearch(event.currentTarget.value)}
            />
          </FormField>
        </div>

        <div className="content-header-right mb-1">
          {/* task-27: the due-date and accounts-status filters belong to the Accounts client grid, which filters the
              same fields. Manage lists clients, so it keeps only the filters that describe the client itself. */}
          <Select
            className="w-150"
            openOnFocus
            panelClass="myPanelClass"
            value={clientTypeFilter}
            onChange={(value) => onClientTypeChange(value as number | null)}
          >
            <SelectOption value={-1}>Client Type</SelectOption>
            <SelectOption value={0}>Limited Company</SelectOption>
            <SelectOption value={1}>Sole Trader</SelectOption>
            <SelectOption value={2}>Partnership</SelectOption>
            <SelectOption value={3}>LLP</SelectOption>
          </Select>
          <Select
            className="w-150"
            openOnFocus
            panelClass="myPanelClass"
            value={clientStatusFilter}
            disabled={showingDeleted}
            onChange={(value) => onClientStatusChange(value as number | null)}
          >
            <SelectOption value={-1}>All</SelectOption>
            <SelectOption value={1}>Active</SelectOption>
            <SelectOption value={0}>Inactive</SelectOption>
          </Select>
          <Select
            className="w-150"
            openOnFocus
            panelClass="myPanelClass"
            value={recordStatusFilter}
            onChange={(value) => onRecordStatusChange(value as number | null)}
          >
            <SelectOption value={0}>Active Clients</SelectOption>
            <SelectOption value={1}>Deleted Clients</SelectOption>
          </Select>
          <div className="toolbar">
            <Button title="Export to Excel" onClick={() => onExportRequested(ExportFormatCode.Excel)}>
              <Icon name="border_all" className="icon-color-xls" />
            </Button>
            <Button title="Export to CSV" onClick={() => onExportRequested(ExportFormatCode.Csv)}>
              <Icon name="description" outlined className="icon-color-xls" />
            </Button>
            <Button title="Export to PDF" onClick={() => onExportRequested(ExportFormatCode.Pdf)}>
              <Icon name="picture_as_pdf" outlined className="icon-color-pdf" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
